import React,{useEffect,useMemo,useState} from 'react';
import {AppState,Platform,useWindowDimensions} from 'react-native';
import {View,Text,StyleSheet,ScrollView,TouchableOpacity} from '../components/rn';
import {useSafeAreaInsets} from 'react-native-safe-area-context';
import {useTheme} from '../theme/ThemeContext';
import {radius,spacing} from '../theme';
import {profilePalette} from '../theme/profileSection';
import {useTranslation} from '../i18n/I18nContext';
import PageHeader from '../components/PageHeader';
import ProfileSection from '../components/ProfileSection';
import {InfoButton,InfoText,useInfo} from '../components/InfoHint';
import DiscoverMapCanvas from '../components/DiscoverMapCanvas';
import {mapPoints} from '../components/discoverMapGeometry';
import {createDiscoverMapSession,type DiscoverMapState} from '../media/discoverMapSession';
import {discoverMapRequest,discoverMapInterest} from '../services/api';

type Props={token:string;myId:number;onClose:()=>void;onOpenProfile:(userId:number)=>void};
const browserDocument=(globalThis as unknown as {document?:{visibilityState:string;
  addEventListener:(name:string,handler:()=>void)=>void;removeEventListener:(name:string,handler:()=>void)=>void}}).document;
export default function DiscoverMapScreen({token,myId,onClose,onOpenProfile}:Props){
  const colors=useTheme(),{t,lang}=useTranslation(),insets=useSafeAreaInsets();
  const palette=profilePalette(colors),info=useInfo();
  const {height}=useWindowDimensions();
  const canvasHeight=Math.max(220,Math.min(480,height*0.48));
  const [state,setState]=useState<DiscoverMapState>({status:'idle',shares:[]});
  const [foreground,setForeground]=useState(AppState.currentState==='active');
  const [selected,setSelected]=useState<number|null>(null),[fit,setFit]=useState(0),[failed,setFailed]=useState(false);
  const session=useMemo(()=>createDiscoverMapSession({fetchPage:(_id,after,signal)=>discoverMapRequest(token,after,signal),
    interest:(ids,signal)=>discoverMapInterest(token,ids,signal),publish:setState}),[token]);
  useEffect(()=>{const sub=AppState.addEventListener('change',value=>setForeground(value==='active'));
    const browser=()=>setForeground(browserDocument?.visibilityState==='visible');
    if(Platform.OS==='web'){browser();browserDocument?.addEventListener('visibilitychange',browser);}
    return()=>{sub.remove();if(Platform.OS==='web')browserDocument?.removeEventListener('visibilitychange',browser);};
  },[]);
  useEffect(()=>{session.setContext(myId,true,foreground);return()=>session.dispose();},[session,myId,foreground]);
  const points=useMemo(()=>mapPoints(state.shares),[state.shares]);
  const visible=foreground&&state.status!=='error'&&points.length>0;
  const openProfile=(shareId:number)=>{
    if(!foreground||state.status!=='ready')return;
    const share=state.shares.find(s=>s.id===shareId);
    if(!share||!Number.isSafeInteger(share.sharerId)||share.sharerId<=0||share.sharerId===myId
      ||(share.expiresAt!==null&&!(Date.parse(share.expiresAt)>Date.now())))return;
    setSelected(shareId);
    onOpenProfile(share.sharerId);
  };
  return <View testID="map-screen" style={[styles.screen,{backgroundColor:colors.white,paddingBottom:insets.bottom}]}>
    <PageHeader title={t('map.title')} onBack={onClose} backTestID="map-close"
      action={<InfoButton open={info.open} onPress={info.toggle} subject={t('map.title')} testID="map-info"/>}/>
    <ScrollView testID="map-content" style={styles.content} contentContainerStyle={styles.listContent}>
    <InfoText open={info.open} text={t('map.hint')} testID="map-info-text" style={styles.hint}/>
    <ProfileSection testID="map-panel">
    {visible&&!failed?<View testID="map-canvas" style={[styles.canvas,{height:canvasHeight}]}><DiscoverMapCanvas points={points} selected={selected} fit={fit}
      onSelect={openProfile} onFailure={()=>setFailed(true)} label={t('map.title')}/></View>:
      <View testID="map-status" style={[styles.empty,{minHeight:canvasHeight}]}><Text style={[styles.status,{color:colors.gray600}]}>{t(failed?'map.providerError':state.status==='error'?'map.error':
        state.status==='loading'?'common.loading':state.shares.length?'map.waiting':'map.empty')}</Text></View>}
    <View style={[styles.actions,{borderColor:colors.gray200}]}>
      <TouchableOpacity accessibilityRole="button" accessibilityState={{disabled:!points.length}}
        disabled={!points.length} testID="map-fit" style={[styles.action,!points.length&&styles.disabled]}
        onPress={()=>{setSelected(null);setFit(n=>n+1);}}>
        <Text style={[styles.actionLabel,{color:palette.heading}]}>{t('map.fit')}</Text>
      </TouchableOpacity>
      <TouchableOpacity accessibilityRole="button" testID="map-retry" style={styles.action}
        onPress={()=>{setFailed(false);void session.refresh();}}>
        <Text style={[styles.actionLabel,{color:palette.heading}]}>{t('common.retry')}</Text>
      </TouchableOpacity>
    </View>
    </ProfileSection>
      {foreground&&state.shares.length>0&&<ProfileSection testID="map-shares" separated>
      {state.shares.map(s=><TouchableOpacity key={s.id} testID={`map-share-${s.id}`} accessibilityRole="button"
        accessibilityState={{selected:selected===s.id,disabled:state.status!=='ready'}}
        disabled={state.status!=='ready'} onPress={()=>openProfile(s.id)}
        style={[styles.row,{backgroundColor:selected===s.id?palette.selectedSurface:colors.white}]}>
        <Text style={[styles.name,{color:selected===s.id?palette.selectedText:palette.heading}]}>{s.name??t('map.unknown')}</Text>
        <Text style={[styles.detail,{color:selected===s.id?palette.selectedText:colors.gray600}]}>{s.pointAt?t('map.lastPoint',{time:new Date(s.pointAt).toLocaleString(lang)}):t('map.waiting')}</Text>
      </TouchableOpacity>)}
      </ProfileSection>}
    </ScrollView>
  </View>;
}
const styles=StyleSheet.create({screen:{flex:1},content:{flex:1},hint:{paddingHorizontal:spacing.md,paddingTop:spacing.md},
  canvas:{minHeight:220,borderRadius:radius.control,overflow:'hidden'},
  empty:{alignItems:'center',justifyContent:'center',padding:spacing.md},
  status:{fontSize:16,lineHeight:24,textAlign:'center',flexShrink:1},
  actions:{flexDirection:'row',flexWrap:'wrap',borderTopWidth:StyleSheet.hairlineWidth,marginTop:spacing.md},
  action:{flexGrow:1,flexBasis:110,minWidth:0,minHeight:48,justifyContent:'center',padding:12},
  actionLabel:{fontSize:16,fontWeight:'500',textAlign:'center',flexShrink:1},disabled:{opacity:0.5},
  listContent:{paddingBottom:spacing.md},row:{padding:12,borderRadius:radius.control,minHeight:64,minWidth:0},
  name:{fontSize:16,fontWeight:'600',marginBottom:4,flexShrink:1},detail:{fontSize:14,lineHeight:20,flexShrink:1}});
